import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Spinner, Alert, InputGroup, Table } from "react-bootstrap";
import * as cobrosFamiliaApi from "../../api/cobrosFamilia";
import { formatoNumero } from "../../utils/moneda";

const TIPOS = [
  { value: "Efectivo", label: "Efectivo" },
  { value: "TarjetaCredito", label: "Tarjeta de crédito" },
];

const NUEVO = "__nuevo__";

/** Alta de un cobro familiar. El deudor es un miembro propio (no un usuario del sistema, ej.
 * "Ruben Cardenas") que se selecciona de la lista o se crea al vuelo. El acreedor siempre es
 * quien crea el cobro. Efectivo: descripción manual + monto. Tarjeta: tarjeta propia + UN gasto
 * esporádico del mes como referencia (la descripción se toma del motivo del gasto, no se
 * escribe) — el monto es el que el miembro debe pagar de ese gasto (puede ser una parte, ej.
 * un ítem de $60 compartido donde solo cobras $15; el resto lo asume el dueño de la tarjeta),
 * así que un mismo gasto puede repartirse en varios cobros. */
export default function ModalNuevoCobro({ show, onHide, deudores, cuentasTarjeta, mes, anio, onGuardado, onDeudorCreado }) {
  const [tipo, setTipo] = useState("TarjetaCredito");
  const [deudorId, setDeudorId] = useState("");
  const [nombreNuevoDeudor, setNombreNuevoDeudor] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [monto, setMonto] = useState("");
  const [cuentaId, setCuentaId] = useState("");
  const [gastosDisponibles, setGastosDisponibles] = useState([]);
  const [gastoSeleccionado, setGastoSeleccionado] = useState("");
  const [montoTarjeta, setMontoTarjeta] = useState("");
  const [cargandoGastos, setCargandoGastos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setTipo("TarjetaCredito");
      setDeudorId("");
      setNombreNuevoDeudor("");
      setDescripcion("");
      setMonto("");
      setCuentaId("");
      setGastosDisponibles([]);
      setGastoSeleccionado("");
      setMontoTarjeta("");
      setError("");
    }
  }, [show]);

  useEffect(() => {
    if (tipo === "TarjetaCredito" && cuentaId) {
      setCargandoGastos(true);
      cobrosFamiliaApi.listarGastosDisponibles(cuentaId, mes, anio)
        .then((data) => { setGastosDisponibles(data); setGastoSeleccionado(""); })
        .finally(() => setCargandoGastos(false));
    } else {
      setGastosDisponibles([]);
      setGastoSeleccionado("");
    }
  }, [tipo, cuentaId, mes, anio]);

  const gastoInfo = gastosDisponibles.find((g) => String(g.gastoEsporadicoId) === gastoSeleccionado);

  useEffect(() => {
    setMontoTarjeta(gastoInfo ? String(gastoInfo.monto) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gastoSeleccionado]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      let idDeudorFinal = deudorId;
      if (deudorId === NUEVO) {
        if (!nombreNuevoDeudor.trim()) throw new Error("Ingresa el nombre del miembro.");
        const creado = await cobrosFamiliaApi.crearDeudor(nombreNuevoDeudor.trim());
        idDeudorFinal = creado.deudorId;
        onDeudorCreado?.({ deudorId: idDeudorFinal, nombre: nombreNuevoDeudor.trim() });
      }

      await cobrosFamiliaApi.registrarCobro({
        deudorId: Number(idDeudorFinal),
        tipo,
        descripcion: tipo === "Efectivo" ? descripcion : (gastoInfo?.motivo || ""),
        monto: tipo === "Efectivo" ? Number(monto) : Number(montoTarjeta),
        cuentaId: tipo === "TarjetaCredito" ? Number(cuentaId) : null,
        mes,
        anio,
        gastoEsporadicoId: tipo === "TarjetaCredito" ? Number(gastoSeleccionado) : null,
      });
      onGuardado();
    } catch (err) {
      setError(err.response?.data?.mensaje || err.message || "No se pudo registrar el cobro.");
    } finally {
      setGuardando(false);
    }
  };

  const puedeGuardar = (deudorId && deudorId !== NUEVO ? true : deudorId === NUEVO && nombreNuevoDeudor.trim())
    && (tipo === "Efectivo"
      ? descripcion && Number(monto) > 0
      : cuentaId && gastoSeleccionado && Number(montoTarjeta) > 0 && Number(montoTarjeta) <= (gastoInfo?.monto ?? 0));

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Nuevo cobro familiar</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Tipo</Form.Label>
            <div className="d-flex gap-3">
              {TIPOS.map((t) => (
                <Form.Check key={t.value} type="radio" name="tipoCobro" label={t.label}
                  checked={tipo === t.value} onChange={() => setTipo(t.value)} />
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Miembro (a quién le cobras)</Form.Label>
            <Form.Select value={deudorId} onChange={(e) => setDeudorId(e.target.value)} required>
              <option value="">Selecciona...</option>
              {deudores.map((d) => (
                <option key={d.deudorId} value={d.deudorId}>{d.nombre}</option>
              ))}
              <option value={NUEVO}>+ Nuevo miembro...</option>
            </Form.Select>
            {deudorId === NUEVO && (
              <Form.Control className="mt-2" value={nombreNuevoDeudor} onChange={(e) => setNombreNuevoDeudor(e.target.value)}
                placeholder="Ej. Ruben Cardenas" autoFocus required />
            )}
          </Form.Group>

          {tipo === "Efectivo" ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required placeholder="Ej. Préstamo para el súper" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Monto</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control type="number" step="0.01" min="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} required />
                </InputGroup>
              </Form.Group>
            </>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Tarjeta de crédito</Form.Label>
                <Form.Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} required>
                  <option value="">Selecciona...</option>
                  {cuentasTarjeta.map((c) => (
                    <option key={c.cuentaId} value={c.cuentaId}>{c.banco} — {c.alias || c.moneda}</option>
                  ))}
                </Form.Select>
                {!cuentasTarjeta.length && <Form.Text className="text-muted">No tienes tarjetas de crédito registradas.</Form.Text>}
              </Form.Group>

              {cuentaId && (
                <Form.Group>
                  <Form.Label>Gasto esporádico del mes en esta tarjeta</Form.Label>
                  {cargandoGastos ? <Spinner size="sm" animation="border" /> : (
                    <div className="fs-card p-0" style={{ maxHeight: 320, overflowY: "auto" }}>
                      <Table size="sm" hover className="table-fs mb-0 align-middle">
                        <tbody>
                          {gastosDisponibles.map((g) => (
                            <tr key={g.gastoEsporadicoId} onClick={() => setGastoSeleccionado(String(g.gastoEsporadicoId))} style={{ cursor: "pointer" }}>
                              <td style={{ width: 30 }} onClick={(e) => e.stopPropagation()}>
                                <Form.Check type="radio" name="gastoSeleccionado" checked={gastoSeleccionado === String(g.gastoEsporadicoId)}
                                  onChange={() => setGastoSeleccionado(String(g.gastoEsporadicoId))} />
                              </td>
                              <td>{g.motivo}</td>
                              <td>{new Date(g.fecha).toLocaleDateString("es-ES")}</td>
                              <td className="text-end">${formatoNumero(g.monto)}</td>
                            </tr>
                          ))}
                          {!gastosDisponibles.length && (
                            <tr><td colSpan={4} className="text-center text-muted py-3">No hay gastos esporádicos disponibles para esta tarjeta en el mes.</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Form.Group>
              )}

              {gastoSeleccionado && (
                <Form.Group className="mt-3">
                  <Form.Label>Monto que debe pagar</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control type="number" step="0.01" min="0.01" max={gastoInfo?.monto}
                      value={montoTarjeta} onChange={(e) => setMontoTarjeta(e.target.value)} required />
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Costo total del ítem: ${formatoNumero(gastoInfo?.monto)}. Si le corresponde menos, ajusta el monto — el resto lo asume el dueño de la tarjeta.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={guardando || !puedeGuardar}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
